/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Iconify } from 'src/components';
import { buildTranscriptParagraphs, findTranscriptMatches } from './transcriptPresentation';
import HighlightedText from './HighlightedText';
import { highlightRanges, speakerOf } from './callReviewModel';

// `extracted`: the server's hints (callReviewModel.readExtracted); its words are highlighted
// along with times, numbers and booking words, until a search takes over the marks.
// `compact` (call review window): one Find box with previous/next only.
// `speakers`: { 'Speaker A': 'Reception', 'Speaker B': 'Patient' } or null.
export default function CallTranscriptReader({ text, extracted, compact, speakers }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [fontSize, setFontSize] = useState(16);
  const [source, setSource] = useState(false);
  const selected = useRef(null);
  const matches = useMemo(() => findTranscriptMatches(text, query), [text, query]);
  const marks = useMemo(() => highlightRanges(text, extracted, 'transcript'), [text, extracted]);
  const ranges = useMemo(
    () => (source ? [{ start: 0, end: text?.length || 0 }] : buildTranscriptParagraphs(text)),
    [source, text]
  );
  useEffect(() => {
    setActive(0);
  }, [text]);
  useEffect(() => {
    selected.current?.scrollIntoView?.({ block: 'nearest', behavior: 'auto' });
  }, [active, query]);
  if (typeof text !== 'string' || !text.trim())
    return <Typography color="text.secondary">No transcript available.</Typography>;
  const matchLabel = matches.length
    ? `${Math.min(active + 1, matches.length)} of ${matches.length} matches`
    : (query && 'No matches') || 'Search saved text';
  const renderRange = ({ start: from0, end }) => {
    // "Caller: …" paragraphs get the speaker in bold (not in Source text view).
    const speaker = source ? null : speakerOf(text.slice(from0, end));
    const start = speaker ? from0 + speaker.start : from0;
    // "Speaker A" is what the provider returns; "Reception"/"Patient" is what the
    // person reading it needs. The map is the server's (transcript_speakers) and is
    // empty whenever the sides could not be told apart, so an unmapped label shows
    // exactly as it came.
    const label = speaker ? (
      <Box component="strong" key="speaker" sx={{ mr: 0.5 }}>
        {speakers?.[speaker.speaker] || speaker.speaker}:
      </Box>
    ) : null;
    if (!matches.length) return [label, <HighlightedText key="text" text={text} ranges={marks} start={start} end={end} />];
    const pieces = [label];
    let cursor = start;
    matches.forEach((match, index) => {
      if (match.end <= start || match.start >= end) return;
      const from = Math.max(start, match.start);
      const to = Math.min(end, match.end);
      pieces.push(text.slice(cursor, from));
      pieces.push(
        <Box
          component="mark"
          key={from}
          ref={index === active ? selected : null}
          sx={{
            bgcolor: index === active ? 'primary.main' : 'action.selected',
            color: index === active ? 'primary.contrastText' : 'text.primary',
            borderRadius: 0.5,
          }}
        >
          {text.slice(from, to)}
        </Box>
      );
      cursor = to;
    });
    pieces.push(text.slice(cursor, end));
    return pieces;
  };
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label="Find in transcript"
          size="small"
          fullWidth
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
        />
        <IconButton
          sx={{ width: 44, height: 44 }}
          aria-label="Clear search"
          disabled={!query}
          onClick={() => {
            setQuery('');
            setActive(0);
          }}
        >
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" aria-live="polite" sx={{ mr: 'auto' }}>
          {compact && !query ? '' : matchLabel}
        </Typography>
        <IconButton
          sx={{ width: 44, height: 44 }}
          aria-label="Previous match"
          disabled={!matches.length}
          onClick={() => setActive((active + matches.length - 1) % matches.length)}
        >
          <Iconify icon="eva:arrow-upward-fill" />
        </IconButton>
        <IconButton
          sx={{ width: 44, height: 44 }}
          aria-label="Next match"
          disabled={!matches.length}
          onClick={() => setActive((active + 1) % matches.length)}
        >
          <Iconify icon="eva:arrow-downward-fill" />
        </IconButton>
        {!compact && <TextField
          select
          size="small"
          label="Text size"
          value={fontSize}
          onChange={(event) => setFontSize(Number(event.target.value))}
          sx={{ minWidth: 90 }}
        >
          {[16, 18, 20].map((size) => (
            <MenuItem key={size} value={size}>
              {size}px
            </MenuItem>
          ))}
        </TextField>}
        {!compact && (
          <Button sx={{ minHeight: 44 }} size="small" onClick={() => setSource(!source)}>
            {source ? 'Paragraph view' : 'Source text'}
          </Button>
        )}
      </Stack>
      <Box
        data-testid="transcript-text"
        sx={{
          fontSize,
          lineHeight: 1.75,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          maxWidth: '78ch',
          '& p': { m: 0, mb: source ? 0 : 2 },
        }}
      >
        {ranges.map((range) => (
          <Box component="p" key={range.start}>
            {renderRange(range)}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
